import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { FormFieldDependency } from '@shared/models/interfaces/form-field-dependency.model';
import { Subscription } from 'rxjs';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { FormValidationError } from '@shared/models/interfaces/form-validation-error.interface';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { isEqual } from 'lodash-es';

/**
 * Gets a specific FormControl from a given FormGroup and control's name
 * @param form
 * @param controlName
 */
export const getControl = (form: FormGroup, controlName: string): FormControl => {
    return form.get(controlName) as FormControl;
};

export const getGroup = (form: FormGroup, controlName: string): FormGroup => {
    const abstractControl = form.get(controlName);
    return isFormGroup(abstractControl) ? (abstractControl as FormGroup) : null;
};

export const getFormArray = (form: FormGroup, arrayPath: string): FormArray => {
    return form.get(arrayPath) as FormArray;
};

export const isFormGroup = (c: AbstractControl | null | undefined): c is FormGroup => {
    return c instanceof FormGroup;
};

export const checkBoxHelper = (
    form: FormGroup,
    targetPath: string,
    ckPath: string,
    ckTargetValue: number,
    subFormPath: string,
    suppCondition = true,
    errorStatus?: string,
    errorMessage?: string
): void => {
    const subGroup = getGroup(form, subFormPath);
    const targetControl = getControl(subGroup, targetPath);
    const targetNameSplitted = targetPath?.split('.');
    const errorName = targetNameSplitted[targetNameSplitted.length - 1];
    const ckValue = toNum(getControl(subGroup, ckPath)?.value);
    const errors = targetControl.errors ? targetControl.errors : {};
    const showError = isNilOrEmpty(targetControl?.value) && ckValue === ckTargetValue && suppCondition;

    addRemoveError(showError, errors, errorName, {
        status: errorStatus || REQUIRED_STATUS,
        message: errorMessage ? errorMessage : undefined,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

export const addRemoveError = (addError: boolean, errors: ValidationErrors, errorKey: string, errorBody: FormValidationError) => {
    const sameErrorBody = (a: FormValidationError, b: FormValidationError): boolean => {
        if (a.status !== b.status) {
            return false;
        }

        if (b.message !== undefined) {
            return a.message === b.message;
        }

        return true;
    };

    const next: ValidationErrors = errors ?? {};
    if (addError) {
        next[errorKey] = errorBody;
        return next;
    }

    if (errorKey in next && sameErrorBody(next[errorKey], errorBody)) {
        delete next[errorKey];
    }

    return next;
};

/**
 * Manages form dependent fields
 * @param form
 * @param depsArray
 * @param formSub
 */
export const manageFormDependencies = (form: FormGroup, depsArray: FormFieldDependency[], formSub: Subscription): void => {
    depsArray.forEach((root) => {
        const rootControl = getControl(form, root.rootFieldName);
        if (rootControl) {
            root.dependentFields.forEach((branch) => {
                const branchControl = getControl(form, branch.name);
                if (branch.conditionFn(rootControl.value)) {
                    branchControl.enable();
                    branchControl.markAsDirty();
                    branchControl.markAsTouched();
                } else {
                    branchControl.disable();
                }
            });
            formSub.add(
                rootControl.valueChanges.subscribe((rootValue) => {
                    root.dependentFields.forEach((branch) => {
                        const branchControl = getControl(form, branch.name);
                        if (branch.conditionFn(rootValue)) {
                            branchControl.enable();
                            branchControl.markAsDirty();
                            branchControl.markAsTouched();
                        } else {
                            branchControl.disable();
                        }
                    });
                })
            );
        }
    });
};

export const rebuildFormArray = <T extends object>(
    fb: FormBuilder,
    storeItems: T[] | null | undefined,
    form: FormGroup,
    formPath: string,
    factory: (fb: FormBuilder, item: T) => FormGroup
): void => {
    const formArrayTarget = getFormArray(form, formPath);
    if (isNilOrEmpty(storeItems)) {
        if (formArrayTarget.length > 0) formArrayTarget.clear();
        return;
    }
    if (isEqual(storeItems, formArrayTarget.getRawValue())) return;
    formArrayTarget.clear();
    storeItems.forEach((item) => {
        formArrayTarget.push(factory(fb, item));
    });
};
